import { t } from 'i18next';
import { ChevronRight } from 'lucide-react';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { PieceIcon } from '@/features/pieces/components/piece-icon';
import { PieceMetadataModel } from '@activepieces/pieces-framework';

import {
  catalogCategoriesOf,
  CatalogCategory,
} from '../lib/catalog-categories';
import {
  catalogCategoryLabel,
  pieceAuthLabel,
  triggerModeLabel,
} from '../lib/catalog-labels';
import { triggerModeOf } from '../lib/trigger-mode';

export type CatalogBreadcrumbLink = {
  label: string;
  to: string;
};

type CatalogBreadcrumbProps = {
  links: CatalogBreadcrumbLink[];
  current?: string;
};

export const CatalogBreadcrumb = ({
  links,
  current,
}: CatalogBreadcrumbProps) => (
  <nav
    aria-label={t('Breadcrumb')}
    className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground"
  >
    {links.map((link, index) => (
      <Fragment key={link.to}>
        {index > 0 && <ChevronRight className="size-3.5" />}
        <Link to={link.to} className="hover:text-foreground">
          {link.label}
        </Link>
      </Fragment>
    ))}
    {current && (
      <>
        <ChevronRight className="size-3.5" />
        <span className="text-foreground">{current}</span>
      </>
    )}
  </nav>
);

type PieceFact = {
  label: string;
  value: string;
};

const PieceFacts = ({ facts }: { facts: PieceFact[] }) => (
  <dl className="flex flex-wrap gap-y-4">
    {facts.map((fact) => (
      <div
        key={fact.label}
        className="flex min-w-[10rem] flex-col gap-1 border-l px-6 first:border-l-0 first:pl-0"
      >
        <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {fact.label}
        </dt>
        <dd className="text-sm font-medium">{fact.value}</dd>
      </div>
    ))}
  </dl>
);

const factsOf = (piece: PieceMetadataModel): PieceFact[] => {
  const triggerMode = triggerModeOf(
    Object.values(piece.triggers).map((trigger) => trigger.type),
  );
  const facts: (PieceFact | null)[] = [
    { label: t('Authentication'), value: pieceAuthLabel(piece.auth) },
    triggerMode
      ? { label: t('Triggering'), value: triggerModeLabel(triggerMode) }
      : null,
    { label: t('Version'), value: piece.version },
  ];
  return facts.filter((fact): fact is PieceFact => fact !== null);
};

type PieceCatalogDetailHeaderProps = {
  piece: PieceMetadataModel;
  catalogPath: string;
  categoryPath: (category: CatalogCategory) => string;
};

export const PieceCatalogDetailHeader = ({
  piece,
  catalogPath,
  categoryPath,
}: PieceCatalogDetailHeaderProps) => {
  // A piece listed under several categories is filed under its first one.
  const [primaryCategory] = catalogCategoriesOf(piece);

  return (
    <div className="flex flex-col gap-6">
      <CatalogBreadcrumb
        links={[
          { label: t('Catalog'), to: catalogPath },
          {
            label: catalogCategoryLabel(primaryCategory),
            to: categoryPath(primaryCategory),
          },
        ]}
        current={piece.displayName}
      />
      <div className="flex items-center gap-4">
        <PieceIcon
          logoUrl={piece.logoUrl}
          displayName={piece.displayName}
          size="xxl"
          border
          showTooltip={false}
        />
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-3xl font-semibold">{piece.displayName}</h1>
          <p className="text-muted-foreground">{piece.description}</p>
        </div>
      </div>
      <PieceFacts facts={factsOf(piece)} />
    </div>
  );
};
